import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import {
    AirportConfigurationType,
    GetGetConfigurationsByTypeResponse,
    KeyGetGetConfigurationsByType,
    useMutationDeleteDeleteConfiguration,
} from '../../../../../backend';
import { queryClient } from '../../../../../Root';
import { errorToastConfig } from '../../../../../utils/utils';
import { useConfirmDialog } from '../../../../confirm/useConfirmDialog';
import { Checkbox } from '../../../../form/Checkbox/Checkbox';
import { ConfigurationsPageFormValues } from '../../../../pages/ConfigurationPage';
import { StyledCheckbox } from '../../../../Resources/ResourceContent/ResourceFields/ResourceFieldsLine/LineContainer/ResourceFieldsLineProperties.style';
import { StyledLineContainerBox } from './ConfigurationLineContainer.style';
import { ComponentByType } from './ConfigurationLineContainer.utils';

export const ConfigurationLineContainer = memo(function ConfigurationLineContainer({
    configurationType,
    index,
    arrayReplace,
    arrayRemove,
    selectedConfigurations,
    setSelectedConfigurations,
}: {
    configurationType: AirportConfigurationType;
    index: number;
    arrayReplace: (index: number, value: any) => void;
    arrayRemove: (index: number) => void;
    selectedConfigurations: number[];
    setSelectedConfigurations: Dispatch<SetStateAction<number[]>>;
}) {
    const Component = ComponentByType[configurationType];
    const { values, dirty, initialValues } = useFormikContext<ConfigurationsPageFormValues>();
    const confirm = useConfirmDialog({ disableReject: true });

    const { mutate } = useMutationDeleteDeleteConfiguration({
        onError: () => {
            toast(<FormattedMessage id={'configurationsForm.error.deleteConfiguration'} />, errorToastConfig);
        },
        onSuccess: (r, requestArgs) => {
            queryClient.setQueriesData(
                {
                    queryKey: [
                        KeyGetGetConfigurationsByType,
                        {
                            type: configurationType,
                            includeNonActive: true,
                        },
                    ],
                },
                (values: GetGetConfigurationsByTypeResponse) => ({
                    ...values,
                    data: values.data.filter(r => r.uuid !== requestArgs.uuid),
                }),
            );
        },
    });

    const handleSelection = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedConfigurations(selectedConfigurations.concat(index));
            } else {
                setSelectedConfigurations(selectedConfigurations.filter(i => i !== index));
            }
        },
        [index, selectedConfigurations, setSelectedConfigurations],
    );

    const handleSelectChange = useCallback(() => {
        arrayReplace(index, {
            ...values.configurations[index],
            active: !values.configurations[index].active,
        });
    }, [arrayReplace, index, values.configurations]);

    const handleDelete = useCallback(
        (type: AirportConfigurationType, uuid: string) => {
            confirm({
                title: (
                    <FormattedMessage
                        id={'configurations.actions.delete'}
                        values={{ type: <FormattedMessage id={'form.value.type.' + type} /> }}
                    />
                ),
                content: uuid && dirty ? <FormattedMessage id={'configurations.alert.delete'} /> : null,
                confirmationText: <FormattedMessage id={'confirm.default.delete'} />,
                cancellationText: <FormattedMessage id={'confirm.default.cancel'} />,
            }).then(() => {
                if (uuid) {
                    mutate({ uuid });
                }
                arrayRemove(index);
                setSelectedConfigurations(selectedConfigurations.filter(i => i !== index));
            });
        },
        [arrayRemove, confirm, dirty, index, mutate, selectedConfigurations, setSelectedConfigurations],
    );

    return (
        <StyledLineContainerBox
            className={classNames({
                selected: selectedConfigurations.includes(index),
                existing: !!values.configurations[index].uuid,
            })}
        >
            <Grid container>
                <Grid width={'50px'} display={'flex'} justifyContent={'center'} alignItems={'center'} color={'white'}>
                    {values.configurations[index]?.defaultConfig ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <FormattedMessage id={'configurations.default.value'} />
                        </Box>
                    ) : (
                        <Checkbox
                            label={''}
                            checked={selectedConfigurations.includes(index)}
                            onChange={handleSelection}
                            disabled={values.configurations[index]?.defaultConfig}
                            sx={{ visibility: values.configurations[index]?.defaultConfig ? 'hidden' : undefined }}
                        />
                    )}
                </Grid>
                {Component !== undefined ? <Component index={index} arrayReplace={arrayReplace} /> : null}
                <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <StyledCheckbox
                        label={''}
                        checked={values.configurations[index].active}
                        onChange={handleSelectChange}
                        modified={
                            values.configurations[index].uuid
                                ? values.configurations[index].active !== initialValues.configurations[index]?.active
                                : undefined
                        }
                        disabled={values.configurations[index]?.defaultConfig}
                        sx={{ visibility: values.configurations[index]?.defaultConfig ? 'hidden' : undefined }}
                    />
                </Grid>
                <Grid width={'52px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <IconButton
                        aria-label="delete"
                        component="label"
                        onClick={() => handleDelete(configurationType, values.configurations[index].uuid)}
                        disabled={values.configurations[index]?.defaultConfig}
                        sx={{ visibility: values.configurations[index]?.defaultConfig ? 'hidden' : undefined }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </StyledLineContainerBox>
    );
});
